import React, { useEffect } from 'react';

const ContactEnhancer = () => {
  useEffect(() => {
    const enhanceContactPage = () => {
      const textColumn = document.getElementById('contact-us-column1');
      const socialsColumn = document.getElementById('contact-us-socials');

      if (!textColumn || !socialsColumn) return;

      // Check if already enhanced to avoid duplicates
      if (textColumn.hasAttribute('data-contact-enhanced') || socialsColumn.hasAttribute('data-contact-enhanced')) {
        return;
      }

      // Enhance the text column
      enhanceTextColumn(textColumn);

      // Enhance the socials column with icons
      enhanceSocialsColumn(socialsColumn);
    };

    const enhanceTextColumn = (textColumn) => {
      // Make text column full height and width of container
      textColumn.style.cssText = `
        width: 100% !important;
        height: 100% !important;
        display: flex !important;
        flex-direction: column !important;
      `;

      const textContent = textColumn.querySelector('.BlockDefault_text__uquKZ');
      if (textContent) {
        textContent.style.cssText = `
          width: 100% !important;
          height: 100% !important;
          display: flex !important;
          align-items: center !important;
          justify-content: flex-start !important;
          text-align: flex-start !important;
          padding: 2rem !important;
          margin: 0 !important;
          box-sizing: border-box !important;
        `;

        const paragraph = textContent.querySelector('p');
        if (paragraph) {
          paragraph.style.cssText = `
            font-size: 1.2rem !important;
            line-height: 1.7 !important;
            color: var(--matterColor, #333) !important;
            margin: 0 !important;
            max-width: 100% !important;
            text-align: start !important;
            font-weight: 400 !important;
          `;
        }
      }

      textColumn.setAttribute('data-contact-enhanced', 'true');
    };

    const enhanceSocialsColumn = (socialsColumn) => {
      // Make socials column full height and width of container
      socialsColumn.style.cssText = `
        width: 100% !important;
        height: 100% !important;
        display: flex !important;
        flex-direction: column !important;
      `;

      const socialsContent = socialsColumn.querySelector('.BlockDefault_text__uquKZ');
      if (!socialsContent) return;

      // Style the container to fill available space
      socialsContent.style.cssText = `
        width: 100% !important;
        height: 100% !important;
        display: flex !important;
        flex-direction: column !important;
        justify-content: center !important;
        align-items: center !important;
        padding: 2rem !important;
        margin: 0 !important;
        box-sizing: border-box !important;
      `;

      const paragraph = socialsContent.querySelector('p');
      if (!paragraph) return;

      // Hide the original paragraph
      paragraph.style.display = 'none';

      // Extract links and create new styled version
      const links = paragraph.querySelectorAll('a');
      const linksData = Array.from(links).map(link => ({
        href: link.href,
        text: link.textContent.trim(),
        target: link.target,
        rel: link.rel
      }));

      // Create new styled links container
      const newLinksContainer = document.createElement('div');
      newLinksContainer.style.cssText = `
        display: flex;
        flex-direction: column;
        gap: 2rem;
        width: 100%;
        max-width: 400px;
      `;

      linksData.forEach(linkData => {
        const linkContainer = document.createElement('div');
        linkContainer.style.cssText = `
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          transition: all 0.3s ease;
          cursor: pointer;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 1px solid rgba(0, 0, 0, 0.05);
          background-color: var(--matterColorLight, #f8f9fa);
        `;

        // Add hover effect
        linkContainer.addEventListener('mouseenter', () => {
          linkContainer.style.transform = 'translateX(8px)';
        });

        linkContainer.addEventListener('mouseleave', () => {
          linkContainer.style.transform = 'translateX(0)';
        });

        // Create icon container with proper styling
        const iconContainer = document.createElement('div');
        iconContainer.style.cssText = `
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: var(--marketplaceColor, #1B5E20);
          border-radius: 12px;
          flex-shrink: 0;
        `;

        // Create icon using proper SVG files
        const icon = document.createElement('div');
        icon.style.cssText = `
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        `;

        // Load and display appropriate SVG icon
        const loadSVGIcon = async (iconPath) => {
          try {
            const response = await fetch(iconPath);
            const svgText = await response.text();

            // Create a temporary div to parse the SVG
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = svgText;
            const svgElement = tempDiv.querySelector('svg');

            if (svgElement) {
              // Style the SVG to fit the container
              svgElement.style.cssText = `
                width: 100%;
                height: 100%;
              `;

              // Handle different SVG structures
              if (iconPath.includes('email.svg')) {
                // For email SVG, make the circle white and paths marketplace color
                const circles = svgElement.querySelectorAll('circle');
                circles.forEach(circle => {
                  circle.style.fill = 'white';
                });

                const paths = svgElement.querySelectorAll('path');
                paths.forEach(path => {
                  path.style.fill = 'var(--marketplaceColor, #1B5E20)';
                });
              } else {
                // For other SVGs (Facebook, Instagram), make everything white
                const paths = svgElement.querySelectorAll('path');
                paths.forEach(path => {
                  path.style.fill = 'white';
                });

                const circles = svgElement.querySelectorAll('circle');
                circles.forEach(circle => {
                  circle.style.fill = 'white';
                });
              }

              icon.appendChild(svgElement);
            }
          } catch (error) {
            console.error('Error loading SVG icon:', error);
            // Fallback to text if SVG fails to load
            icon.innerHTML = linkData.href.includes('mailto:') ? '✉' :
                            linkData.href.includes('facebook') ? 'f' :
                            linkData.href.includes('instagram') ? '📷' : '🔗';
            icon.style.color = 'white';
            icon.style.fontSize = '20px';
            icon.style.fontWeight = 'bold';
          }
        };

        // Set icon based on link type using proper SVG files
        if (linkData.href.includes('mailto:')) {
          loadSVGIcon('/static/appicons/email.svg');
        } else if (linkData.href.includes('facebook')) {
          loadSVGIcon('/static/appicons/facebook.svg');
        } else if (linkData.href.includes('instagram')) {
          loadSVGIcon('/static/appicons/instagram.svg');
        } else {
          // Fallback for other link types
          icon.innerHTML = '🔗';
          icon.style.color = 'white';
          icon.style.fontSize = '20px';
        }

        iconContainer.appendChild(icon);

        // Create content section
        const contentContainer = document.createElement('div');
        contentContainer.style.cssText = `
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          gap: 0.25rem;
          text-align: left;
        `;

        // Create label
        const label = document.createElement('div');
        label.style.cssText = `
          font-size: 0.9rem;
          color: var(--marketplaceColor, #1B5E20);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        `;

        if (linkData.href.includes('mailto:')) {
          label.textContent = 'EMAIL US';
        } else if (linkData.href.includes('facebook')) {
          label.textContent = 'FOLLOW US';
        } else if (linkData.href.includes('instagram')) {
          label.textContent = 'FOLLOW US';
        }

        // Create link text
        const linkElement = document.createElement('div');
        linkElement.style.cssText = `
          color: var(--matterColor, #333);
          text-decoration: none;
          font-weight: 600;
          font-size: 1.1rem;
        `;

        // Clean up the display text
        let displayText = linkData.text;
        if (linkData.href.includes('mailto:')) {
          displayText = linkData.text; // Keep email as is
        } else {
          displayText = linkData.text; // Keep social handles as is
        }
        linkElement.textContent = displayText;

        // Add click handler to container
        linkContainer.addEventListener('click', () => {
          window.open(linkData.href, linkData.target || '_blank');
        });

        contentContainer.appendChild(label);
        contentContainer.appendChild(linkElement);
        linkContainer.appendChild(iconContainer);
        linkContainer.appendChild(contentContainer);

        newLinksContainer.appendChild(linkContainer);
      });

      // Insert the new container after the paragraph
      paragraph.parentNode.insertBefore(newLinksContainer, paragraph.nextSibling);

      socialsColumn.setAttribute('data-contact-enhanced', 'true');
    };

    // Run immediately and after a short delay to handle dynamic content
    enhanceContactPage();
    const timeoutId = setTimeout(enhanceContactPage, 500);
    const timeoutId2 = setTimeout(enhanceContactPage, 1000);
    const timeoutId3 = setTimeout(enhanceContactPage, 2000);

    // Listen for navigation changes
    const handleNavigation = () => {
      setTimeout(enhanceContactPage, 100);
      setTimeout(enhanceContactPage, 500);
    };

    // Listen for popstate (back/forward navigation)
    window.addEventListener('popstate', handleNavigation);

    // Listen for DOM changes that might indicate new content
    const observer = new MutationObserver(() => {
      setTimeout(enhanceContactPage, 100);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false
    });

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
      window.removeEventListener('popstate', handleNavigation);
      observer.disconnect();
    };
  }, []);

  return null; // This component doesn't render anything
};

export default ContactEnhancer;